import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getComercialLost } from '../../db/api.js'
import Icon from '../../components/icons.jsx'
import TableSort from './TableSort.jsx'

const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
const date = value => value ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'

export default function LostOpportunities({ query }) {
  const [sort, setSort] = useState({ field: 'data_fechamento', direction: 'desc' })
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const dialog = useRef(null)
  const scroll = useRef(null)
  const sentinel = useRef(null)
  const cursor = useRef(0)
  const busy = useRef(false)
  const generation = useRef(0)
  const queryKey = JSON.stringify({ ...query, sort: sort.field, direction: sort.direction })

  const load = useCallback(async (reset = false) => {
    if (busy.current && !reset) return
    const id = generation.current
    busy.current = true
    setLoading(true)
    setError(null)
    try {
      const result = await getComercialLost({ ...JSON.parse(queryKey), offset: reset ? 0 : cursor.current })
      if (id !== generation.current) return
      cursor.current = (reset ? 0 : cursor.current) + result.items.length
      if (!result.items.length) cursor.current = result.total
      setTotal(result.total)
      setRows(current => {
        const known = new Set(reset ? [] : current.map(row => row.id))
        return [...(reset ? [] : current), ...result.items.filter(row => !known.has(row.id))]
      })
    } catch {
      if (id === generation.current) setError('Não foi possível carregar as negócios perdidos.')
    } finally {
      if (id === generation.current) { busy.current = false; setLoading(false) }
    }
  }, [queryKey])

  useEffect(() => {
    if (!expanded) return
    generation.current += 1
    cursor.current = 0
    if (scroll.current) scroll.current.scrollTop = 0
    setRows([])
    setTotal(0)
    load(true)
    return () => { generation.current += 1 }
  }, [load, expanded])

  useEffect(() => {
    if (!expanded) return
    const element = dialog.current
    element.showModal()
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { element.close(); document.body.style.overflow = previous }
  }, [expanded])

  useEffect(() => {
    if (!expanded || loading || error || !sentinel.current || cursor.current >= total) return
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) load()
    }, { root: scroll.current, rootMargin: '60px' })
    observer.observe(sentinel.current)
    return () => observer.disconnect()
  }, [expanded, loading, error, total, rows, load])

  const table = (large = false) => <div className={`cd-table-scroll cd-risk-scroll${large ? ' cd-risk-scroll-expanded' : ''}`} ref={large ? scroll : undefined} onScroll={event => {
    const el = event.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && !error && cursor.current < total) load()
  }}>
    <table className="cd-risk-table">
      {large && <colgroup>{[20, 10, 12, 13, 10, 13, 22].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}</colgroup>}
      <thead><tr>{[['titulo', 'Oportunidade'], ['valor', 'Valor'], ['etapa', 'Etapa'], ['responsavel', 'Responsável'], ['data_fechamento', 'Perdido em'], ['motivo', 'Motivo'], ['justificativa', 'Descrição do motivo']].map(([field, label]) => <th key={field} aria-sort={sort.field === field ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={field} sort={sort} onChange={setSort}>{label}</TableSort></th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row.id}>
        <td><Link to={`/comercial/pipelines?deal=${encodeURIComponent(row.id)}`} target="_blank" rel="noopener noreferrer">{row.titulo}</Link></td>
        <td>{money(row.valor)}</td><td>{row.etapa}</td><td>{row.responsavel}</td><td>{date(row.data_fechamento)}</td><td className="cd-loss-reason-cell">{row.motivo}</td><td className="cd-loss-description-cell">{row.justificativa || 'Sem descrição informada'}</td>
      </tr>)}</tbody>
    </table>
    {!rows.length && !loading && !error && <div className="cd-empty">Nenhum negócio perdido no período.</div>}
    {(loading || error || cursor.current < total) && <div className="cd-risk-more" aria-live="polite" ref={large ? sentinel : undefined}>
      {loading ? 'Carregando oportunidades…' : <>{error && <span>{error} </span>}<button type="button" onClick={() => load()}>{error ? 'Tentar novamente' : 'Carregar mais 5'}</button></>}
    </div>}
  </div>

  return <>
    <button type="button" className="cd-risk-expand" onClick={() => setExpanded(true)}>Ver negócios perdidos <Icon n="arrow_right" s={14} /></button>
    {expanded && <dialog ref={dialog} className="cd-risk-dialog cd-lost-dialog" aria-labelledby="cd-lost-title" onCancel={() => setExpanded(false)} onClose={() => setExpanded(false)}>
      <header><h2 id="cd-lost-title">Negócios perdidos <span className="cd-count-badge">{total}</span></h2><button type="button" autoFocus aria-label="Fechar negócios perdidos" onClick={() => setExpanded(false)}>×</button></header>
      {table(true)}
    </dialog>}
  </>
}
